import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Wordmark } from "./Logo";
import { Button } from "./ui/button";
import { LogOut, HelpCircle, ArrowRightLeft, Rocket, ChevronRight } from "lucide-react";
import { DASH_IMG } from "../lib/brand";
import { InstallButton } from "./InstallPrompt";
import Onboarding from "./Onboarding";
import HowToUse from "./HowToUse";
import StartHereModal from "./StartHereModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

export default function Shell({ children, title, subtitle, actions, bgImage }) {
  const { user, logout, actAs, startActingAs, stopActingAs, viewFlash, setViewFlash } = useAuth();
  const navigate = useNavigate();
  const [howOpen, setHowOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const isLenderView = isAdmin && !actAs;

  useEffect(() => {
    if (!viewFlash) return;
    const t = setTimeout(() => setViewFlash(null), 2200);
    return () => clearTimeout(t);
  }, [viewFlash, setViewFlash]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const homeTarget = actAs ? "/dashboard" : isAdmin ? "/lender" : "/dashboard";

  const enterAgentView = async () => {
    if (actAs) return;
    let id = localStorage.getItem("mpl_last_agent_id");
    let name = localStorage.getItem("mpl_last_agent_name");
    if (!id) {
      try {
        const { data } = await api.get("/admin/agents");
        const chosen = (data || []).find((a) => a.status === "active") || (data || [])[0];
        if (chosen) { id = chosen.id; name = chosen.name; }
      } catch (e) { /* ignore */ }
    }
    if (!id) {
      toast("Add or approve an agent first, then switch to Agent view.");
      return;
    }
    startActingAs({ id, name: name || "your agent" });
    navigate("/dashboard");
    setViewFlash({ title: "Now in Agent view 🎬", sub: `You're now acting as ${name || "an agent"} — send on their behalf.` });
  };
  const exitAgentView = () => {
    if (isLenderView) return;
    stopActingAs();
    navigate("/lender");
    setViewFlash({ title: "Back to Lender view 🏦", sub: "Let's get some escrows! 🤝" });
  };

  return (
    <div className="mpl-grain min-h-screen bg-[#0a0a0a] relative overflow-x-hidden">
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src={bgImage || DASH_IMG}
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-[0.45] scale-105 mpl-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/30 via-[#0a0a0a]/72 to-[#0a0a0a]/92" />
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-5%,rgba(255,59,48,0.16),transparent_55%)]" />
      </div>
      <Onboarding />
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate(homeTarget)} data-testid="nav-home-button">
            <Wordmark />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            {isAdmin && (
              <div className="flex items-center rounded-full border border-white/15 overflow-hidden text-xs" data-testid="view-mode-toggle">
                <button
                  onClick={exitAgentView}
                  data-testid="view-mode-lender"
                  className={`px-3 py-1.5 font-semibold transition-colors ${isLenderView ? "bg-[#ff3b30] text-white" : "text-white/60 hover:text-white"}`}
                >
                  Lender
                </button>
                <button
                  onClick={enterAgentView}
                  data-testid="view-mode-agent"
                  className={`px-3 py-1.5 font-semibold transition-colors ${actAs ? "bg-emerald-500 text-white" : "text-white/60 hover:text-white"}`}
                >
                  Agent
                </button>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHowOpen(true)}
              data-testid="how-it-works-button"
              className="text-white/80 hover:text-white hover:bg-white/10 border border-white/15 rounded-full px-2.5 sm:px-3"
            >
              <HelpCircle className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">How it works</span>
            </Button>
            <InstallButton variant="shell" />
            <span className="hidden md:inline text-sm text-white/60">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={doLogout}
              data-testid="logout-button"
              className="text-white/70 hover:text-white hover:bg-white/10 px-2.5 sm:px-3"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
        {actAs && (
          <div className="bg-emerald-500/15 border-t border-emerald-400/30" data-testid="acting-as-banner">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-2 flex items-center justify-between gap-3">
              <p className="text-xs text-emerald-200 flex items-center gap-2 min-w-0">
                <ArrowRightLeft className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Acting on behalf of <strong>{actAs.name}</strong> — sends go out as them.</span>
              </p>
              <button onClick={exitAgentView} data-testid="exit-agent-view" className="text-xs font-semibold text-white underline underline-offset-2 shrink-0">
                Back to Lender view
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12 overflow-x-hidden">
        <button
          onClick={() => setStartOpen(true)}
          data-testid="start-here-banner"
          className="group w-full mb-8 rounded-2xl border border-[#ff3b30]/45 bg-gradient-to-r from-[#ff3b30] to-[#ff5147] px-5 py-4 sm:py-5 flex items-center justify-between gap-4 shadow-[0_16px_46px_-10px_rgba(255,59,48,0.65)] hover:from-[#ff5147] hover:to-[#ff6a61] transition-all mpl-pulse-glow"
        >
          <span className="flex items-center gap-3.5 min-w-0">
            <span className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Rocket className="h-6 w-6 text-white" />
            </span>
            <span className="text-left min-w-0">
              <span className="block font-display text-xl sm:text-2xl tracking-tight text-white leading-none">
                {isLenderView ? "Lenders" : "Realtors"} — Start Here
              </span>
              <span className="block text-white/90 text-xs sm:text-sm mt-1.5">
                New here? Tap for your easy 30-second setup 🍭
              </span>
            </span>
          </span>
          <ChevronRight className="h-6 w-6 text-white shrink-0 group-hover:translate-x-1 transition-transform" />
        </button>

        {(title || actions) && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 mpl-rise">
            <div className="min-w-0">
              {title && <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none mpl-shadow-text break-words">{title}</h1>}
              {subtitle && <p className="text-white/75 mt-3 max-w-xl mpl-shadow-text">{subtitle}</p>}
            </div>
            {actions}
          </div>
        )}
        {children}
      </main>

      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent
          data-testid="how-it-works-dialog"
          className="bg-[#0f0f0f]/95 backdrop-blur-xl border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">How My Pocket Lender works 🍭</DialogTitle>
          </DialogHeader>
          <HowToUse isLender={isLenderView} />
        </DialogContent>
      </Dialog>

      <StartHereModal open={startOpen} isLender={isLenderView} onClose={() => setStartOpen(false)} />

      {viewFlash && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center px-6 pointer-events-none"
          data-testid="view-switch-flash"
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm mpl-flash-bg" />
          <div className="relative text-center mpl-flash-pop">
            <h2 className="font-display text-4xl sm:text-6xl tracking-tighter text-white drop-shadow-[0_6px_30px_rgba(0,0,0,0.7)]">
              {viewFlash.title}
            </h2>
            <p className="mt-4 text-lg sm:text-2xl text-white/85 font-medium max-w-xl mx-auto">{viewFlash.sub}</p>
          </div>
        </div>
      )}
    </div>
  );
}
